import { Bars3Icon } from '@heroicons/react/24/outline';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import { Money, RowAction, RowActions, Switch } from '../../../components/ui';
import { Thumb } from '../../MenuItems/components/Thumb';
import type { ItemRecord, RestaurantInfo } from '../../MenuItems/types';

interface Props {
    item: ItemRecord;
    name: string;
    restaurant: RestaurantInfo | null;
    /** Items move between categories only: the API keeps no order inside one. */
    dragHandle: DraggableProvidedDragHandleProps | null;
    dragging: boolean;
    /** Edit link; absent for roles that cannot edit. */
    editTo?: string;
    onToggleAvailable: (next: boolean) => void;
    onRemove?: () => void;
}

export const ItemRow = ({ item, name, restaurant, dragHandle, dragging, editTo, onToggleAvailable, onRemove }: Props) => {
    const { t } = useTranslation('menu');
    const actions: RowAction[] = [
        ...(editTo ? [{ id: 'edit', label: t('editor.itemEdit'), to: editTo }] : []),
        ...(onRemove
            ? [
                  {
                      id: 'remove',
                      label: t('editor.removeFromCategory'),
                      onSelect: onRemove,
                      tone: 'danger' as const,
                      separated: true,
                  },
              ]
            : []),
    ];
    return (
        <div className={`flex min-h-16 items-center gap-2 rounded-control bg-surface py-1 pe-1 ${dragging ? 'shadow-e2' : ''}`}>
            {dragHandle ? (
                <button
                    type="button"
                    {...dragHandle}
                    aria-label={t('editor.moveItem', { name })}
                    className="flex size-11 shrink-0 cursor-grab items-center justify-center rounded-control text-muted hover:bg-hover"
                >
                    <Bars3Icon className="size-5" aria-hidden="true" />
                </button>
            ) : (
                <span className="w-1 shrink-0" />
            )}
            <Thumb url={item.imageUrl} />
            <div className="min-w-0 flex-1">
                <p className="truncate text-small font-semibold text-strong">{name}</p>
                {!item.isAvailable && <p className="text-caption text-muted">{t('items.soldOut')}</p>}
            </div>
            {restaurant && (
                <Money
                    amount={item.price}
                    symbol={restaurant.currencySymbol}
                    position={restaurant.currencySymbolPosition}
                    className="shrink-0 text-small text-strong"
                />
            )}
            <Switch checked={item.isAvailable} onChange={onToggleAvailable} label={<span className="sr-only">{t('items.availableFor', { name })}</span>} />
            {actions.length > 0 && <RowActions actions={actions} label={name} />}
        </div>
    );
};
